import {
  buildManageAccountUrl,
  STRONG_AUTH_INTENT,
} from "@arcadeai/ui-kit/utils/auth-redirect";
import { useSyncExternalStore } from "react";

// RFC 9470's error code, sent by the Coordinator and Engine in a
// `WWW-Authenticate` challenge when a sign-in didn't use strong authentication.
const INSUFFICIENT_USER_AUTHENTICATION_RE =
  /\berror="insufficient_user_authentication"/;

export function isStrongAuthChallenge(wwwAuthenticate: string | null): boolean {
  return (
    wwwAuthenticate !== null &&
    INSUFFICIENT_USER_AUTHENTICATION_RE.test(wwwAuthenticate)
  );
}

// Once refused, every later request is refused too, until the operator signs
// in again, which reloads the page.
let required = false;
const listeners = new Set<() => void>();

export function reportStrongAuthRequired(): void {
  if (required) {
    return;
  }
  required = true;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether Arcade has refused this sign-in for not using strong authentication. */
export function isStrongAuthRequired(): boolean {
  return required;
}

export function useStrongAuthRequired(): boolean {
  return useSyncExternalStore(subscribe, isStrongAuthRequired, () => false);
}

/**
 * Manage my account, opened to set up strong authentication and then come
 * back to `returnTo`.
 */
export function strongAuthSetupUrl(
  accountUiUrl: string,
  returnTo: string
): string {
  return buildManageAccountUrl(accountUiUrl, {
    intent: STRONG_AUTH_INTENT,
    returnTo,
  });
}

/**
 * Ends the Dashboard session, then opens `setupUrl`. Stays put and returns
 * false when the session couldn't be ended: coming back through the login page
 * would reuse it and be refused again.
 */
export async function leaveForStrongAuthSetup({
  setupUrl,
  signOut,
  navigate,
}: {
  setupUrl: string;
  signOut: () => Promise<{ error: unknown }>;
  navigate: (url: string) => void;
}): Promise<boolean> {
  try {
    const { error } = await signOut();
    if (error) {
      return false;
    }
  } catch {
    return false;
  }
  navigate(setupUrl);
  return true;
}
